// ---------------------------------------------------------------------------
// The browser side of INVOICING, MARGIN AND THE PAYROLL EXPORT. V32.
//
// NO ARITHMETIC HERE OR IN THE SCREEN. Every figure arrives computed —
// `projected_charge_minor`, `projected_pay_minor`, `projected_margin_minor`,
// `margin_basis_points`, `total_minor` — and the screen's only job is to put a
// decimal point in and draw it. `verify-V32.sh` greps this directory for the
// operators after stripping comments, for `routes/pay.ts`'s reason: a second
// engine disagreeing with the first at the third decimal place is the kind of
// thing an agency notices in month three.
//
// NO VOCABULARY IS SPELLED HERE EITHER. A rate basis arrives as its id and the
// server's own phrasing of what it means; a correction reason arrives as an id;
// a payroll format arrives with its country, its published specification and its
// delimiter. Nothing in this directory decides what any of them mean.
//
// AND `not_priceable_reason` IS A SENTENCE FROM THE DATABASE, never assembled
// here. Absolute rule 6: an automated figure a person cannot have explained to
// them is unfinished, and a requirement dropped from a margin calculation is
// exactly the kind of quiet omission that needs a sentence attached.
// ---------------------------------------------------------------------------

import { mutate, read } from "./console";

export type MarginRequirement = {
  requirement_id: string;
  role_name: string;
  zone: string | null;
  headcount: number;
  minutes_each: string;
  minutes_total: string;
  currency: string;
  charge_rate_basis: string;
  charge_rate_minor: string;
  /** The unconditional base rate of the pinned pay rule set. Null is what makes
   *  a requirement unpriceable, and the null is the answer rather than a gap. */
  base_rate_minor: string | null;
  projected_charge_minor: string | null;
  projected_pay_minor: string | null;
  projected_margin_minor: string | null;
  is_priceable: boolean;
  not_priceable_reason: string | null;
};

export type MarginSummary = {
  requirements_total: number;
  /** Never equal to `requirements_total` by construction. The gap is the point:
   *  what could not be priced is dropped from the denominator, never scored
   *  zero, and the screen has to say how many. */
  requirements_priceable: number;
  currency: string | null;
  projected_charge_minor: string;
  projected_pay_minor: string;
  projected_margin_minor: string;
  margin_basis_points: number | null;
  budget_minor: string | null;
  budget_currency: string | null;
  over_budget: boolean | null;
};

export type InvoiceRow = {
  id: string;
  reference: string;
  currency: string;
  issued_at: string;
  proof_pack_id: string | null;
  supersedes_id: string | null;
  correction_reason: string | null;
  is_superseded: boolean;
  line_count: string;
};

export type InvoiceLine = {
  seq: number;
  label: string;
  timesheet_id: string;
  /** Exactly one of these two (0209): a labour charge, or a recharged expense claim. */
  charge_calculation_id: string | null;
  expense_claim_id: string | null;
  amount_minor: string;
  /** Null on an expense line. */
  payable_minutes: string | null;
  /** 0278. Frozen with the line; `not_stated` on a line issued before VAT was recorded. */
  vat_treatment?: string;
  vat_rate?: string;
};

/** 0278. One (treatment, rate) group, its VAT rounded once. */
export type VatGroup = { vat_treatment: string; label: string; vat_rate: string; net_minor: string; vat_minor: string };
export type VatTreatment = { id: string; label: string; has_rate: boolean };

export type InvoiceDocument = {
  id: string;
  reference: string;
  currency: string;
  event_id: string;
  event_name: string;
  client_account_id: string | null;
  proof_pack_id: string | null;
  supersedes_id: string | null;
  correction_reason: string | null;
  is_superseded: boolean;
  issued_at: string;
  /** The sum of the lines, before VAT. */
  total_minor: string;
  lines: InvoiceLine[];
  vat_minor?: string;
  gross_minor?: string;
  vat?: VatGroup[];
};

export type PayrollGap = { seq: number; column_name: string; note: string };

export type PayrollFormat = {
  id: string;
  country_code: string;
  name: string;
  /** The published document this format claims to follow. A payroll format with
   *  no named source is a guess, and the screen shows it beside every file. */
  spec_reference: string;
  spec_version: string;
  delimiter: string;
  is_enabled: boolean;
  column_count: string;
  gaps: PayrollGap[];
};

export type PayrollExport = {
  run: { id: string; row_count: number; gross_total_minor: string } | null;
  format_id: string;
  delimiter: string;
  columns: string[];
  row_count: number;
  payload_sha256: string;
  payload: string;
};

export const invoicing = {
  margin: (base: string, eventId: string) =>
    read(base, `/v1/events/${encodeURIComponent(eventId)}/margin`),
  invoices: (base: string, eventId: string) =>
    read(base, `/v1/events/${encodeURIComponent(eventId)}/invoices`),
  invoice: (base: string, invoiceId: string) =>
    read(base, `/v1/invoices/${encodeURIComponent(invoiceId)}`),
  vatTreatments: (base: string) => read(base, "/v1/reference/vat-treatments"),
  issue: (base: string, eventId: string, body: Record<string, unknown>) =>
    mutate("POST", base, `/v1/events/${encodeURIComponent(eventId)}/invoices`, body),
  formats: (base: string) => read(base, "/v1/payroll/formats"),
  takeExport: (base: string, body: Record<string, unknown>) =>
    mutate("POST", base, "/v1/payroll/exports", body),
};
